import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';

interface SystemSettings {
  contact_email: string;
  support_email: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  smtp_use_ssl: boolean;
  email_notifications_enabled: boolean;
  email_from_address: string;
  email_from_name: string;
  mailgun_api_key: string;
  mailgun_domain: string;
  mailgun_region: string;
  email_provider: string;
}

interface TestEmailResult {
  success: boolean;
  message: string;
  smtp_host: string;
  smtp_port: number;
}

const AdminSystemSettingsPage: React.FC = () => {
  const { userRole } = useAuth();
  const [settings, setSettings] = useState<SystemSettings>({
    contact_email: '',
    support_email: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    email_notifications_enabled: true,
    email_from_address: '',
    email_from_name: 'RateMyTalk',
    mailgun_api_key: '',
    mailgun_domain: '',
    mailgun_region: 'us',
    email_provider: 'smtp'
  });
  const [originalSettings, setOriginalSettings] = useState<SystemSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'email' | 'smtp' | 'test'>('email');
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<TestEmailResult | null>(null);
  const [useCurrentSettings, setUseCurrentSettings] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (userRole === 'admin') {
      loadSettings();
    }
  }, [userRole]);

  useEffect(() => {
    if (originalSettings) {
      const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasUnsavedChanges(hasChanges);
    }
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/admin/settings');
      const loadedSettings = response.data.data.settings;

      setSettings(loadedSettings);
      setOriginalSettings(loadedSettings);
    } catch (err: any) {
      console.error('Failed to load settings:', err);
      setError(err.response?.data?.error?.message || 'Failed to load system settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof SystemSettings, value: string | number | boolean) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(null);
    setError(null);
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Only send changed fields
      const changedFields: Partial<SystemSettings> = {};
      Object.keys(settings).forEach(key => {
        const field = key as keyof SystemSettings;
        if (originalSettings && settings[field] !== originalSettings[field]) {
          (changedFields as any)[field] = settings[field];
        }
      });

      if (Object.keys(changedFields).length === 0) {
        setSuccess('No changes to save');
        return;
      }

      const response = await apiClient.put('/v1/admin/settings', changedFields);
      const updatedSettings = response.data.data.settings;

      setSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      setSuccess(`Settings updated successfully. Updated fields: ${response.data.data.updated_fields.join(', ')}`);
      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.error?.message || 'Failed to save system settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      setError('Please enter a test email address');
      return;
    }

    try {
      setIsTesting(true);
      setError(null);
      setTestResult(null);

      const testData: any = {
        test_email: testEmail.trim(),
        use_current_settings: useCurrentSettings
      };

      // If not using current settings, include the form values
      if (!useCurrentSettings) {
        testData.email_provider = settings.email_provider;
        testData.smtp_host = settings.smtp_host;
        testData.smtp_port = settings.smtp_port;
        testData.smtp_username = settings.smtp_username;
        testData.smtp_password = settings.smtp_password;
        testData.smtp_use_tls = settings.smtp_use_tls;
        testData.smtp_use_ssl = settings.smtp_use_ssl;
        testData.email_from_address = settings.email_from_address;
        testData.email_from_name = settings.email_from_name;
        testData.mailgun_api_key = settings.mailgun_api_key;
        testData.mailgun_domain = settings.mailgun_domain;
        testData.mailgun_region = settings.mailgun_region;
      }

      const response = await apiClient.post('/v1/admin/settings/test-email', testData);
      setTestResult(response.data.data);
    } catch (err: any) {
      console.error('Failed to test email:', err);
      setError(err.response?.data?.error?.message || 'Failed to test email configuration');
    } finally {
      setIsTesting(false);
    }
  };

  const handleResetSettings = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setSuccess(null);
      setError(null);
      setHasUnsavedChanges(false);
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading system settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>
                <i className="fas fa-cog me-2 text-primary"></i>
                System Settings
              </h1>
              <p className="text-muted mb-0">
                Configure system-wide settings for email notifications and SMTP server
              </p>
            </div>
            <div>
              {hasUnsavedChanges && (
                <span className="badge bg-warning text-dark me-2">
                  <i className="fas fa-exclamation-circle me-1"></i>
                  Unsaved Changes
                </span>
              )}
              <button
                className="btn btn-outline-secondary me-2"
                onClick={loadSettings}
                disabled={isSaving}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Success:</strong> {success}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setSuccess(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'email' ? 'active' : ''}`}
                onClick={() => setActiveTab('email')}
              >
                <i className="fas fa-envelope me-2"></i>
                Email Settings
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'smtp' ? 'active' : ''}`}
                onClick={() => setActiveTab('smtp')}
              >
                <i className="fas fa-server me-2"></i>
                Email Provider
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'test' ? 'active' : ''}`}
                onClick={() => setActiveTab('test')}
              >
                <i className="fas fa-paper-plane me-2"></i>
                Test Email
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-envelope me-2"></i>
                  Email Configuration
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="contactEmail" className="form-label">
                        Contact Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="contactEmail"
                        value={settings.contact_email}
                        onChange={(e) => handleInputChange('contact_email', e.target.value)}
                        placeholder="contact@ratemy.talk"
                      />
                      <div className="form-text">
                        Email address displayed for general contact inquiries
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="supportEmail" className="form-label">
                        Support Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="supportEmail"
                        value={settings.support_email}
                        onChange={(e) => handleInputChange('support_email', e.target.value)}
                        placeholder="support@ratemy.talk"
                      />
                      <div className="form-text">
                        Email address displayed for support requests and locked accounts
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="emailFromAddress" className="form-label">
                        From Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="emailFromAddress"
                        value={settings.email_from_address}
                        onChange={(e) => handleInputChange('email_from_address', e.target.value)}
                        placeholder="noreply@ratemy.talk"
                      />
                      <div className="form-text">
                        Email address used as sender for system emails
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="emailFromName" className="form-label">
                        From Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="emailFromName"
                        value={settings.email_from_name}
                        onChange={(e) => handleInputChange('email_from_name', e.target.value)}
                        placeholder="RateMyTalk"
                        maxLength={100}
                      />
                      <div className="form-text">
                        Display name used as sender for system emails
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailNotificationsEnabled"
                        checked={settings.email_notifications_enabled}
                        onChange={(e) => handleInputChange('email_notifications_enabled', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="emailNotificationsEnabled">
                        <strong>Enable Email Notifications System-Wide</strong>
                      </label>
                      <div className="form-text">
                        When disabled, no email notifications will be sent regardless of user preferences
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMTP Configuration Tab */}
      {activeTab === 'smtp' && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-server me-2"></i>
                  Email Provider Configuration
                </h5>
              </div>
              <div className="card-body">
                {/* Email Provider Selection */}
                <div className="row mb-4">
                  <div className="col-12">
                    <label className="form-label">
                      <strong>Email Provider</strong>
                    </label>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="emailProvider"
                            id="providerMailgun"
                            checked={settings.email_provider === 'mailgun'}
                            onChange={() => {
                              handleInputChange('email_provider', 'mailgun');
                              // Set Mailgun defaults
                              handleInputChange('smtp_host', 'smtp.mailgun.org');
                              handleInputChange('smtp_port', 587);
                              handleInputChange('smtp_use_tls', true);
                              handleInputChange('smtp_use_ssl', false);
                            }}
                          />
                          <label className="form-check-label" htmlFor="providerMailgun">
                            <strong>Mailgun</strong> (Recommended)
                          </label>
                          <div className="form-text">
                            Professional email service with API and SMTP support
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="emailProvider"
                            id="providerSmtp"
                            checked={settings.email_provider === 'smtp'}
                            onChange={() => {
                              handleInputChange('email_provider', 'smtp');
                              // Clear Mailgun-specific fields
                              handleInputChange('mailgun_api_key', '');
                              handleInputChange('mailgun_domain', '');
                            }}
                          />
                          <label className="form-check-label" htmlFor="providerSmtp">
                            <strong>Custom SMTP</strong>
                          </label>
                          <div className="form-text">
                            Use any SMTP server (Gmail, Outlook, etc.)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mailgun Configuration */}
                {settings.email_provider === 'mailgun' && (
                  <>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Mailgun Setup:</strong> Get your API key and domain from your
                      <a href="https://app.mailgun.com/app/dashboard" target="_blank" rel="noopener noreferrer" className="ms-1">
                        Mailgun Dashboard <i className="fas fa-external-link-alt"></i>
                      </a>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="mailgunDomain" className="form-label">
                            Mailgun Domain <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="mailgunDomain"
                            value={settings.mailgun_domain}
                            onChange={(e) => handleInputChange('mailgun_domain', e.target.value)}
                            placeholder="mg.yourdomain.com"
                            required
                          />
                          <div className="form-text">
                            Your verified Mailgun domain (e.g., mg.ratemy.talk)
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="mailgunRegion" className="form-label">
                            Mailgun Region
                          </label>
                          <select
                            className="form-select"
                            id="mailgunRegion"
                            value={settings.mailgun_region}
                            onChange={(e) => handleInputChange('mailgun_region', e.target.value)}
                          >
                            <option value="us">US (smtp.mailgun.org)</option>
                            <option value="eu">EU (smtp.eu.mailgun.org)</option>
                          </select>
                          <div className="form-text">
                            Choose based on your Mailgun account region
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <div className="mb-3">
                          <label htmlFor="mailgunApiKey" className="form-label">
                            Mailgun API Key <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="mailgunApiKey"
                            value={settings.mailgun_api_key}
                            onChange={(e) => handleInputChange('mailgun_api_key', e.target.value)}
                            placeholder="key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            required
                          />
                          <div className="form-text">
                            Your Mailgun private API key (starts with "key-")
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Mailgun SMTP Settings:</strong> These are automatically configured for Mailgun
                      <ul className="mb-0 mt-2">
                        <li>Host: {settings.mailgun_region === 'eu' ? 'smtp.eu.mailgun.org' : 'smtp.mailgun.org'}</li>
                        <li>Port: 587 (TLS)</li>
                        <li>Username: postmaster@{settings.mailgun_domain || 'your-domain'}</li>
                        <li>Password: Your Mailgun API key</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Custom SMTP Configuration */}
                {settings.email_provider === 'smtp' && (
                  <>
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Custom SMTP:</strong> Configure your own SMTP server settings.
                      All fields are required for email functionality to work.
                    </div>

                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label htmlFor="smtpHost" className="form-label">
                            SMTP Server Host <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="smtpHost"
                            value={settings.smtp_host}
                            onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                            placeholder="smtp.gmail.com"
                            required
                          />
                          <div className="form-text">
                            Hostname or IP address of your SMTP server
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label htmlFor="smtpPort" className="form-label">
                            SMTP Port <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id="smtpPort"
                            value={settings.smtp_port}
                            onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value) || 587)}
                            min="1"
                            max="65535"
                            required
                          />
                          <div className="form-text">
                            Common: 587 (TLS), 465 (SSL), 25 (plain)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="smtpUsername" className="form-label">
                            SMTP Username <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="smtpUsername"
                            value={settings.smtp_username}
                            onChange={(e) => handleInputChange('smtp_username', e.target.value)}
                            placeholder="your-email@gmail.com"
                            required
                          />
                          <div className="form-text">
                            Username for SMTP authentication
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="smtpPassword" className="form-label">
                            SMTP Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            id="smtpPassword"
                            value={settings.smtp_password}
                            onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                            placeholder="Enter SMTP password"
                            required
                          />
                          <div className="form-text">
                            Password or app-specific password for SMTP authentication
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="smtpUseTls"
                            checked={settings.smtp_use_tls}
                            onChange={(e) => handleInputChange('smtp_use_tls', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="smtpUseTls">
                            <strong>Use TLS (STARTTLS)</strong>
                          </label>
                          <div className="form-text">
                            Recommended for port 587. Encrypts connection after initial handshake.
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="smtpUseSsl"
                            checked={settings.smtp_use_ssl}
                            onChange={(e) => handleInputChange('smtp_use_ssl', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="smtpUseSsl">
                            <strong>Use SSL</strong>
                          </label>
                          <div className="form-text">
                            For port 465. Encrypts entire connection from start.
                          </div>
                        </div>
                      </div>
                    </div>

                    {settings.smtp_use_tls && settings.smtp_use_ssl && (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> Both TLS and SSL are enabled. This may cause connection issues.
                        Typically, you should use either TLS (port 587) or SSL (port 465), but not both.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Tab */}
      {activeTab === 'test' && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-paper-plane me-2"></i>
                  Test Email Configuration
                </h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Test your email configuration</strong> by sending a test email.
                  This helps verify that your SMTP settings are correct before saving.
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="testEmail" className="form-label">
                        Test Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="testEmail"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="admin@example.com"
                        required
                      />
                      <div className="form-text">
                        Email address where the test email will be sent
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Configuration to Test</label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="testSettings"
                          id="useCurrentSettings"
                          checked={useCurrentSettings}
                          onChange={() => setUseCurrentSettings(true)}
                        />
                        <label className="form-check-label" htmlFor="useCurrentSettings">
                          Use saved settings from database
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="testSettings"
                          id="useFormSettings"
                          checked={!useCurrentSettings}
                          onChange={() => setUseCurrentSettings(false)}
                        />
                        <label className="form-check-label" htmlFor="useFormSettings">
                          Use current form values (unsaved)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleTestEmail}
                    disabled={isTesting || !testEmail.trim()}
                  >
                    {isTesting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending Test Email...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Test Email
                      </>
                    )}
                  </button>
                </div>

                {testResult && (
                  <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'}`}>
                    <div className="d-flex align-items-start">
                      <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2 mt-1`}></i>
                      <div className="flex-grow-1">
                        <strong>{testResult.success ? 'Success!' : 'Test Failed'}</strong>
                        <p className="mb-2">{testResult.message}</p>
                        <div className="small">
                          <strong>SMTP Server:</strong> {testResult.smtp_host}:{testResult.smtp_port}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  {hasUnsavedChanges && (
                    <div className="text-warning">
                      <i className="fas fa-exclamation-circle me-1"></i>
                      You have unsaved changes
                    </div>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={handleResetSettings}
                    disabled={isSaving || !hasUnsavedChanges}
                  >
                    <i className="fas fa-undo me-1"></i>
                    Reset Changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveSettings}
                    disabled={isSaving || !hasUnsavedChanges}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemSettingsPage;
